import React from 'react';
import { connect } from 'react-redux';

import ActionsMenu from '~/components/Shared/ActionsMenu/ActionsMenu';
import ConditionRuleInput from '~/components/Workflow/shared/ConditionRuleInput';
import SignerSelect from '~/components/Workflow/shared/SignerSelect';
import { WorkflowPartyType } from '~/enums/WorkflowPartyType';
import { useUsers } from '~/hooks/useUsers';
import { useIntl } from 'react-intl';
import { getUsedSignerIds } from '~/reducers/workflow';
import Divider from '~/ui/components/Divider/Divider';
import FlexLayout from '~/ui/components/FlexLayout/FlexLayout';
import InputLabel from '~/ui/components/InputLabel/InputLabel';
import Text from '~/ui/components/Text/Text';
import { testInvalidSigner } from '~/utils/workflow';

import WorkflowUserRoleSingleSelect from '../../shared/WorkflowUserRoleSingleSelect';
import { testValidApprover } from '../../Workflow.utils';

function Signer({
  signer,
  onUpdateConditionRule,
  onRemoveSigner,
  onUpdateSigner,
  onUpdateSignerUser,
  // connected
  isInvalid,
  usedSignerIds,
}) {
  const {
    id,
    conditionRule,
    default: defaultSigner = null,
    name,
    partyType,
  } = signer;
  const users = useUsers();
  const intl = useIntl();

  let signerName = name;
  if (isInvalid) {
    signerName =
      id === null
        ? intl.formatMessage({
            id: 'Signer.unspecifiedSigner',
            defaultMessage: 'Unspecified Signer',
            description: 'Label for signer without an ID',
          })
        : intl.formatMessage({
            id: 'Signer.invalidSigner',
            defaultMessage: 'Invalid Signer',
            description: 'Label for invalid signer',
          });
  }

  const enableUserSelect = id && partyType === WorkflowPartyType.Party;

  return (
    <FlexLayout
      bg="black-alpha-04"
      flexDirection="column"
      sx={{ borderRadius: 'm' }}
    >
      <FlexLayout alignItems="center" justifyContent="space-between" p={6}>
        <Text color="gray-700" variant="m-dense-bold">
          {signerName}
        </Text>
        <ActionsMenu
          id={`workflow_builder_sign_signer_menu_id=${id}`}
          items={[
            {
              content: intl.formatMessage({
                id: 'Signer.removeSigner',
                defaultMessage: 'Remove signer',
                description: 'Menu item to remove a signer',
              }),
              onClick: () => onRemoveSigner(),
            },
          ]}
          align="end"
        />
      </FlexLayout>
      <Divider />
      <FlexLayout flexDirection="column" p={6} space={6}>
        <FlexLayout alignItems="center" space={6}>
          <InputLabel
            id={`signer-${id}`}
            isRequired
            label={intl.formatMessage({
              id: 'Signer.signerLabel',
              defaultMessage: 'Signer',
              description: 'Label for signer input field',
            })}
          >
            <SignerSelect
              blacklistValues={usedSignerIds}
              value={id}
              onChange={onUpdateSigner}
            />
          </InputLabel>
          {enableUserSelect && (
            <InputLabel
              isRequired
              flex="1 1 auto"
              id={`signer-${id}-user`}
              label={intl.formatMessage({
                id: 'Signer.defaultValueLabel',
                defaultMessage: 'Default Value',
                description: 'Label for default value input field',
              })}
            >
              <WorkflowUserRoleSingleSelect
                error={
                  defaultSigner && !testValidApprover(defaultSigner, users)
                    ? intl.formatMessage({
                        id: 'Signer.usersNotExistError',
                        defaultMessage:
                          "There are users that don't exist anymore",
                        description: 'Error message for non-existent users',
                      })
                    : ''
                }
                placeholder={intl.formatMessage({
                  id: 'Signer.selectUserPlaceholder',
                  defaultMessage: 'Select a user..',
                  description: 'Placeholder text for user selection',
                })}
                value={defaultSigner}
                width="fullWidth"
                onChange={onUpdateSignerUser}
              />
            </InputLabel>
          )}
        </FlexLayout>
        <Divider />
        <ConditionRuleInput
          conditionRule={conditionRule}
          onChange={onUpdateConditionRule}
        />
      </FlexLayout>
    </FlexLayout>
  );
}

function mapStateToProps({ workflow }, { signer }) {
  return {
    isInvalid: testInvalidSigner(signer, workflow.signers),
    usedSignerIds: getUsedSignerIds(workflow),
  };
}

export default connect(mapStateToProps)(Signer);
